import React from 'react';
import { useParams } from 'react-router-dom';
import { useGetWorkshopBySlugQuery } from 'api/workshopApi';
import { useGetMemberBySlugQuery } from 'api/consultancyApi';
import { mediaUrl } from 'utils/mediaUrl';

const WorkshopPost = () => {
  const { slug } = useParams();
  const { data, isLoading, isError } = useGetWorkshopBySlugQuery(slug);
  const workshop = data?.data;

  const hostSlug = workshop?.host_slug;
  const {
    data: hostData,
    isLoading: isHostLoading,
    isError: isHostError,
  } = useGetMemberBySlugQuery(hostSlug, { skip: !hostSlug });

  const host = hostData?.data;

  const getImageUrl = (path) => {
    if (!path) return 'https://via.placeholder.com/800x400?text=No+Image';
    return mediaUrl(path) || 'https://via.placeholder.com/800x400?text=No+Image';
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64 mt-16">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (isError || !workshop) {
    return (
      <div className="max-w-4xl mx-auto mt-16 p-6 bg-red-50 border border-red-200 rounded-lg text-center text-red-600">
        <h2 className="text-xl font-bold mb-2">Oops! Something went wrong.</h2>
        <p>We couldn't load the workshop details. Please try again later.</p>
      </div>
    );
  }

  return (
    <section className="max-w-6xl mx-auto mt-10 md:mt-16 px-4 sm:px-6 lg:px-8 mb-20">
      
      {/* Hero Banner Section */}
      <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
        <div className="relative h-64 sm:h-80 md:h-96 w-full">
          <img
            src={getImageUrl(workshop.cover_photo)}
            alt={workshop.title}
            className="w-full h-full object-cover"
            onError={(e) => { 
              e.target.src = 'data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22800%22%20height%3D%22400%22%20viewBox%3D%220%200%20800%20400%22%3E%3Crect%20fill%3D%22%23eee%22%20width%3D%22800%22%20height%3D%22400%22%2F%3E%3Ctext%20fill%3D%22%23777%22%20font-family%3D%22sans-serif%22%20font-size%3D%2232%22%20dy%3D%2210.5%22%20font-weight%3D%22bold%22%20x%3D%2250%25%22%20y%3D%2250%25%22%20text-anchor%3D%22middle%22%3ENo%20Image%3C%2Ftext%3E%3C%2Fsvg%3E';
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
          
          <div className="absolute bottom-0 left-0 p-6 md:p-10 text-white w-full">
            <span className="inline-block px-3 py-1 bg-blue-600 text-xs font-bold uppercase tracking-wider rounded-full mb-3 shadow-sm">
              Workshop
            </span>
            <h1 className="text-3xl md:text-5xl font-extrabold mb-2 leading-tight shadow-sm text-shadow">
              {workshop.title}
            </h1>
            <p className="text-lg md:text-xl font-medium text-gray-200 max-w-3xl">
              {workshop.subtitle}
            </p>
          </div>
        </div>

        {/* Content Section */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 p-6 md:p-10">
          
          {/* Main Info Column */}
          <div className="lg:col-span-2 space-y-8">
            <div>
              <h3 className="text-xl font-bold text-gray-800 mb-3 border-b pb-2">About This Workshop</h3>
              <p className="text-gray-600 text-lg leading-relaxed">{workshop.tagline}</p>
            </div>

            {/* Host Profile */}
            <div className="bg-blue-50/50 rounded-xl p-6 border border-blue-100">
              <h3 className="text-xl font-bold text-gray-800 mb-6">Meet Your Host</h3>
              {isHostLoading ? (
                <p className="text-gray-500">Loading host profile...</p>
              ) : isHostError ? (
                <p className="text-red-500">Could not load host profile.</p>
              ) : host ? (
                <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6">
                  <div className="w-28 h-28 rounded-full overflow-hidden border-4 border-white shadow-lg flex-shrink-0">
                    <img
                      src={getImageUrl(host.profile_photo)}
                      alt={host.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="text-center sm:text-left">
                    <h4 className="text-2xl font-bold text-gray-900">{host.name}</h4>
                    <p className="text-blue-600 font-medium mt-1 mb-3">{host.tagline}</p>
                    <p className="text-gray-600 text-sm leading-relaxed">{host.short_description}</p>
                  </div>
                </div>
              ) : null}
            </div>
          </div>

          {/* Sidebar / Quick Details */}
          <div className="bg-gray-50 rounded-xl p-6 border border-gray-200 h-max">
            <h3 className="text-lg font-bold text-gray-800 mb-5 border-b border-gray-200 pb-3">Workshop Details</h3>
            
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <span className="text-xl">📅</span>
                <div>
                  <p className="text-xs text-gray-500 font-medium uppercase">Date</p>
                  <p className="font-semibold text-gray-800">{new Date(workshop.date).toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
                </div>
              </li>
              
              <li className="flex items-start gap-3">
                <span className="text-xl">🕒</span>
                <div>
                  <p className="text-xs text-gray-500 font-medium uppercase">Schedule</p>
                  <p className="font-semibold text-gray-800">{workshop.start_time} - {workshop.end_time}</p>
                  <p className="text-sm text-gray-600">({workshop.duration})</p>
                </div>
              </li>

              <li className="flex items-start gap-3">
                <span className="text-xl">📍</span>
                <div>
                  <p className="text-xs text-gray-500 font-medium uppercase">Location</p>
                  <p className="font-semibold text-gray-800">{workshop.location}</p>
                </div>
              </li>

              {workshop.languages && workshop.languages.length > 0 && (
                <li className="flex items-start gap-3">
                  <span className="text-xl">🗣️</span>
                  <div>
                    <p className="text-xs text-gray-500 font-medium uppercase">Language</p>
                    <p className="font-semibold text-gray-800">{Array.isArray(workshop.languages) ? workshop.languages.join(', ') : workshop.languages}</p>
                  </div>
                </li>
              )}
            </ul>

            <div className="mt-8 pt-6 border-t border-gray-200">
              <p className="text-xs text-gray-500 font-medium uppercase mb-1">Registration Fee</p>
              <div className="flex items-baseline gap-3">
                <span className="text-3xl font-extrabold text-blue-600">₹{Number(workshop.course_fee_discounted).toLocaleString('en-IN')}</span>
                <span className="text-lg font-medium text-gray-400 line-through">₹{Number(workshop.course_fee_original).toLocaleString('en-IN')}</span>
              </div>
            </div>
            
            <button className="w-full mt-6 bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-4 rounded-lg shadow-md transition-colors">
              Manage Registrations
            </button>
          </div>
          
        </div>
      </div>
    </section>
  );
};

export default WorkshopPost;
